import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CloseWarning } from "../../../secondary";
import styles from "../characteristicCustomForm.module.scss";
import { Button, Form } from "../../../primary";
import { useForm } from "react-hook-form";
import { CharacteristicCustomFormSingleSelect } from "../characteristicCustomFormSingleSelect";
import { CharacteristicCustomFormMultiSelect } from "../characteristicCustomFormMultiSelect";
import { CharacteristicCustomFormText } from "../characteristicCustomFormText";
import { signedLockedAccess } from "../../../../utils";
import { CANCEL_BTN, CANCEL_ID, SAVE_BTN, SUBMIT_ID } from "../../../../constants";
import { setStatusUpdateForm, updateViewFormAsyn } from "../../../../app/customFormSlice";
import constants from "../../../../constants/constants.json";
import Tooltip from "../../../secondary/Tooltip/tooltip";
import infoIcon from "../../../../static/images/info.svg";
import { ReadOnlyBanner } from "../../../secondary/ReadOnlyBanner/ReadOnlyBanner";

const sortByOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);

export const EditCustomForm = ({
  index,
  name,
  formId,
  queryParam,
  updateDone,
  selectedIndex,
  hideIt,
  assessmentDetails,
  isCompleted,
  customFormBuilder,
  getForm,
  setGetForm,
  clickCancel,
  handleYes,
  handleNo,
  characteristicTabsData,
  setFormChangesConfirm,
  setClickCancel,
  setCustomFormBuilder,
  isReadOnly,
}) => {
  const { register, handleSubmit, errors, reset, setValue, clearErrors, trigger } = useForm({
    mode: "onBlur",
  });
  const dispatch = useDispatch();

  const showViewForm = useSelector((state) => state.forms.viewForms);
  const user = useSelector((state) => state.user);
  const forms = useSelector((state) => state.forms.formDetailsData);

  const [sectionError, setSectionError] = useState([]);
  const sectionRefs = useRef([]);

  const formBuilder = useMemo(() => (forms.formBuilderInfo ? [...forms.formBuilderInfo].sort(sortByOrder) : []), [forms.formBuilderInfo]);
  const formQuestions = useMemo(() => forms?.formBuilderInfo ?? [], [forms?.formBuilderInfo]);
  const sectionInfo = useMemo(() => forms?.sectionInfo ?? [], [forms?.sectionInfo]);
  const questionMap = useMemo(() => new Map(formQuestions.map((q) => [q.id, q])), [formQuestions]);

  const answeredIds = useMemo(() => {
    const data = getForm?.formdata || [];
    return new Set(data.map((d) => d.id));
  }, [getForm]);

  const isQuestionVisibleInBlock = useCallback(
    (question, index, list) => {
      if (index === 0) return true;

      for (let i = 0; i < index; i++) {
        const prev = list[i];

        if (prev?.goto === "next" && !answeredIds.has(prev.id)) {
          return false;
        }
      }

      return true;
    },
    [answeredIds]
  );
  
  const sections = useMemo(
    () =>
      sectionInfo.map((section) => ({
        ...section,
        questions: section.questionsId
          .map((id) => questionMap.get(id))
          .filter(Boolean)
          .sort(sortByOrder),
      })),
    [questionMap, sectionInfo]
  );

  const sectionQuestionIds = useMemo(() => new Set(sectionInfo.flatMap((s) => s.questionsId)), [sectionInfo]);
  const unSectioned = useMemo(() => formQuestions.filter((q) => !sectionQuestionIds.has(q.id)).sort(sortByOrder), [
    formQuestions,
    sectionQuestionIds,
  ]);

  useEffect(() => {
    dispatch(setStatusUpdateForm({}));
    reset({});
    if (formBuilder.length) setCustomFormBuilder(formBuilder);
  }, [dispatch, formBuilder, reset, setCustomFormBuilder]);

  const SelectedVal = useCallback((data) => setGetForm(data), [setGetForm]);

  const buildBranch = useCallback(
    (answers) => {
      const result = [];
      const questions = formBuilder;

      let i = 0;

      while (i < questions.length) {
        const q = questions[i];
        result.push(q);

        const answer = answers?.find((a) => a.id === q.id);

        if (!answer) break;

        let goto = q.goto;

        if (q.options?.length && answer.options?.length) {
          const selectedOpt = q.options.find((o) => o.key === answer.options[0]);
          if (selectedOpt?.goto) {
            goto = selectedOpt.goto;
          }
        }

        if (!goto || goto === "next") {
          i += 1;
          continue;
        }

        if (goto === "eof") break;

        const targetIndex = questions.findIndex((x) => x.id === Number(goto));
        if (targetIndex === -1) break;

        i = targetIndex;
      }

      return result;
    },
    [formBuilder]
  );

  useEffect(() => {
    const answers = getForm?.formdata || [];
    const branch = buildBranch(answers);
  
    setCustomFormBuilder(branch);
  }, [getForm, buildBranch, setCustomFormBuilder]);

  const questionTypeInput = useCallback(
    (data, index, optionData, onAnswerChange) => {
      const props = { data, index, getForm, showViewForm, register, errors, setValue, clearErrors, onAnswerChange };

      switch (data.type) {
        case "singleselect":
          return <CharacteristicCustomFormSingleSelect {...props} singleSelectVal={setGetForm} optionData={optionData} />;
        case "multiselect":
          return (
            <CharacteristicCustomFormMultiSelect
              {...props}
              addItemMultiselect={SelectedVal}
              removeItemMultiSelect={SelectedVal}
              setFormChangesConfirm={setFormChangesConfirm}
              optionData={optionData}
            />
          );
        case "text":
          return <CharacteristicCustomFormText {...props} getTextBoxVal={SelectedVal} />;
        default:
          return null;
      }
    },
    [
      SelectedVal,
      errors,
      getForm,
      register,
      setValue,
      clearErrors,
      showViewForm,
      setFormChangesConfirm,
      setGetForm,
    ]
  );

  const handleAnswerChange = useCallback((sectionName) => {
    setSectionError((prev) => prev.filter((e) => e.sectionName !== sectionName));
  }, []);

  const questionType = useCallback(
    (data, index, sectionName) => {
      const optionData = data.type !== "text" ? data.options.map((opt) => ({ key: opt.key, value: opt.key === "other" ? "Others" : opt.value })) : [];
      return questionTypeInput(data, index, optionData, () => handleAnswerChange(sectionName));
    },
    [questionTypeInput, handleAnswerChange]
  );

  const isAnswered = useCallback(
    (q) =>
      Array.isArray(q.options) ? q.options.some((opt) => typeof opt === "string" && opt.trim()) : typeof q.value === "string" && q.value.trim(),
    []
  );

  const validateMandatorySections = useCallback(
    (answers) => {
      return sectionInfo.reduce((errs, section) => {
        if (!section.isMandatory) return errs;
        const sectionAnswers = answers.filter((ans) => section.questionsId.includes(ans.id));
        if (!sectionAnswers.some(isAnswered)) {
          errs.push({
            sectionName: section.name,
            message: section.questionsId.length > 1 ? constants.customForm.sectionTooltip : constants.customForm.singleQuestionSection,
          });
        }
        return errs;
      }, []);
    },
    [sectionInfo, isAnswered]
  );

  const getSaveFormData = useCallback(
    () => (formBuilder.length ? getForm?.formdata?.filter((item) => formBuilder.some((f) => f.id === item.id)) : []),
    [formBuilder, getForm]
  );

  const handleSectionErrors = useCallback(
    (saveFormData) => {
      const errors = validateMandatorySections(saveFormData);
      setSectionError(errors);
      return errors;
    },
    [validateMandatorySections]
  );

  const onSubmit = useCallback(async () => {
    const saveFormData = getSaveFormData();
    if (handleSectionErrors(saveFormData).length > 0) return;

    const method = showViewForm.formdata?.length > 0 ? "PUT" : "POST";
    const payloadData = { formdata: saveFormData };
    const queryParamList = {
      feature: "NE1 WMS",
      subFeature: "Characteristics",
      ne1Id: queryParam?.ne1Id,
      assessmentId: queryParam?.assessmentId,
    };

    dispatch(updateViewFormAsyn({ id: formId, queryParam, method, data: payloadData }, queryParamList));

    setTimeout(() => {
      updateDone(index);
      selectedIndex(index + 1);
    }, 1500);
  }, [getSaveFormData, handleSectionErrors, showViewForm.formdata?.length, queryParam, dispatch, formId, updateDone, index, selectedIndex]);

  const onSaveForm = useCallback(async () => {
    const isValid = await trigger();
    const saveFormData = getSaveFormData();
    const sectionErrors = handleSectionErrors(saveFormData);

    if (sectionErrors.length > 0) {
      const firstErrorIndex = sectionInfo.findIndex((section) => sectionErrors.some((err) => err.sectionName === section.name));
      const sectionEl = sectionRefs?.current?.[firstErrorIndex];
      sectionEl?.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    if (!isValid || sectionErrors.length > 0) return;

    await onSubmit();
  }, [trigger, getSaveFormData, handleSectionErrors, sectionInfo, onSubmit]);

  const handleCancel = useCallback(() => setClickCancel(true), [setClickCancel]);

  return (
    <>
      {clickCancel && (
        <div className="text-center">
          <CloseWarning message="All progress in this section will be lost. Are you sure want to cancel?" yesFn={handleYes} noFn={handleNo} />
        </div>
      )}
      {user?.role === "ExternalStandardUser" ? (
        <p className={"mt-1"}>Custom form is not selected for this wound</p>
      ) : characteristicTabsData?.[0]?.category === "History/Cause" && characteristicTabsData[0]?.isCompleted === 0 ? (
        <div className={`${styles.errorTissueNull} col-11 text-danger text-center`}>
          {"Please select History/Cause before selecting"} {name}
        </div>
      ) : characteristicTabsData?.[1]?.category === "Tissue Type" && characteristicTabsData[1]?.isCompleted === 0 ? (
        <div className={`${styles.errorTissueNull} col-11 text-danger text-center`}>
          {"Please select Tissue Type(s) before selecting"} {name}
        </div>
      ) : (
        <>
        {isReadOnly && <ReadOnlyBanner />}
          <Form
          setFormChangesConfirm={setFormChangesConfirm}
          formChanges={true}
          className={`pl-2 ${styles.patient_content} submit`}
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="col-sm-12">
            {sections.map((section, sectionIndex) => {
              const error = sectionError.find((e) => e.sectionName === section.name);

              const sectionText = section.sectionPlaceholder ?? constants.customForm.answerAllThatApply;

              return (
                <div key={sectionIndex} className={styles.section} ref={(el) => (sectionRefs.current[sectionIndex] = el)}>
                  <div className={styles.sectionHeader}>
                    {section?.isMandatory === 1 && (
                      <Tooltip
                        placement="right"
                        className={styles.form_tooltip}
                        content={section.questionsId.length > 1 ? constants.customForm.sectionTooltip : constants.customForm.singleQuestionSection}
                      >
                        <img src={infoIcon} alt="info" />
                      </Tooltip>
                    )}
                    <span className={styles.sectionTitle}>
                      {sectionText} {section?.isMandatory === 1 && <span className={styles.astrick}>&#42;</span>}
                    </span>
                  </div>

                  {error && <p className={styles.sectionError}>{error.message}</p>}

                  {section.questions.map((q, i) => {
                    if (!q || !isQuestionVisibleInBlock(q, i, section.questions)) return null;

                    return (
                      <div key={q.id}>
                        {questionType(q, i, section.name)}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {unSectioned?.length > 0 && (
              <div className={styles.section}>
                {unSectioned.map((q, i) => {
                  if (!q || !isQuestionVisibleInBlock(q, i, unSectioned)) return null;

                  return (
                    <div key={q.id}>
                      {questionType(q, i)}
                    </div>
                  );
                })}
              </div>
            )}
            {customFormBuilder?.length > 0 ? (
              hideIt &&
              signedLockedAccess(assessmentDetails?.assessmentStage) && (
                <div className="text-center pb-4">
                  <br />
                  <Button onClick={onSaveForm} classes={{ className: `btn pl-1 mr-4 ${styles.btn_primary}` }} type="submit" id={SUBMIT_ID}>
                    {isCompleted === 0 ? "Next" : SAVE_BTN}
                  </Button>
                  {isCompleted !== 0 && (
                    <Button
                      type="button"
                      classes={{ className: `btn ml-2 ${styles.btn_scondary} handlecancel` }}
                      id={CANCEL_ID}
                      onClick={handleCancel}
                    >
                      {CANCEL_BTN}
                    </Button>
                  )}
                </div>
              )
            ) : (
              <h4 className="text-center">No form</h4>
            )}
          </div>
        </Form>
        </>
      )}
    </>
  );
};
